import { useEffect } from 'react';
import useTemplateStyles from './useTemplateStyles';
import { useSelector } from 'react-redux';
import { getTemplateStyle } from '../../v2/theme/templateStyles';

// Size-option keyword → pixel maps ("medium" matches the pre-customization look)
export const BASE_FONT_SIZES = { small: 14, medium: 16, large: 18 };
export const HEADING_FONT_SIZES = { small: 24, medium: 32, large: 40, xlarge: 48 };
export const BUTTON_FONT_SIZES = { small: 12, medium: 14, large: 16 };
export const LOGO_HEIGHTS = { small: 48, medium: 70, large: 90 };

const DynamicStyles = () => {
  const { template, logo, colors, typography, buttons, layout } = useTemplateStyles();
  const { businessName } = useSelector((store) => store.configs);

  // Template look-and-feel variant (heading character, button case, card style)
  useEffect(() => {
    const root = document.documentElement;
    const style = getTemplateStyle(template);
    root.dataset.template = template || 'Sports';
    root.style.setProperty('--heading-weight', `${style.headingWeight}`);
    root.style.setProperty('--heading-transform', style.headingTransform);
    root.style.setProperty('--heading-letter-spacing', style.headingSpacing);
    root.style.setProperty('--button-transform', style.buttonTransform);
    root.style.setProperty('--button-letter-spacing', style.buttonSpacing);
    root.style.setProperty('--card-radius', style.cardRadius);
    root.style.setProperty('--card-shadow', style.cardShadow);
    root.style.setProperty('--card-hover-lift', style.cardHoverLift);
    root.style.setProperty('--image-zoom', `${style.imageZoom}`);
    root.style.setProperty('--motion-speed', style.motionSpeed);
    root.style.setProperty('--motion-ease', style.motionEase);
    root.style.setProperty('--page-bg', style.pageBg || '#ffffff');
    root.style.setProperty('--surface-tint', style.surfaceTint || '#f8f8f8');
    root.style.setProperty('--image-fit', style.imageFit || 'cover');
  }, [template]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--primary', colors.primary);
    root.style.setProperty('--secondary', colors.secondary);
    root.style.setProperty('--tertiary', colors.tertiary);
    root.style.setProperty('--quaternary', colors.quaternary);
    root.style.setProperty('--headings', colors.headings);
    root.style.setProperty('--body', colors.body);

    root.style.setProperty('--heading-font', typography.headings || 'Poppins, sans-serif');
    root.style.setProperty('--body-font', typography.body || 'Poppins, sans-serif');

    const baseSize = BASE_FONT_SIZES[typography.baseSize] || BASE_FONT_SIZES.medium;
    const h1Size = HEADING_FONT_SIZES[typography.headingSize] || HEADING_FONT_SIZES.medium;
    const buttonSize = BUTTON_FONT_SIZES[typography.buttonSize] || BUTTON_FONT_SIZES.medium;
    root.style.setProperty('--font-size-base', `${baseSize}px`);
    root.style.setProperty('--font-size-h1', `${h1Size}px`);
    root.style.setProperty('--font-size-h2', `${Math.round(h1Size * 0.8125)}px`);
    root.style.setProperty('--font-size-h3', `${Math.round(h1Size * 0.6875)}px`);
    root.style.setProperty('--font-size-h4', `${Math.round(h1Size * 0.5625)}px`);
    root.style.setProperty('--font-size-button', `${buttonSize}px`);

    root.style.setProperty('--navbar-logo-height', `${LOGO_HEIGHTS[logo?.size] || LOGO_HEIGHTS.medium}px`);
    root.style.setProperty('--product-grid-columns', `${layout?.productGridColumns || 4}`);

    const brandPrimary = colors.primary || '#032541';
    const btnPrimary = buttons.primary || brandPrimary;
    root.style.setProperty('--button-primary', btnPrimary);
    // When hover isn't explicitly themed it defaults to black (see template slice), which looks
    // wrong on a coloured button. Fall back to a darker shade of the button's own colour instead.
    const hoverConfigured = buttons.hover && !['#000', '#000000'].includes(buttons.hover.toLowerCase());
    const btnHover = hoverConfigured ? buttons.hover : `color-mix(in srgb, ${btnPrimary} 88%, #000)`;
    root.style.setProperty('--button-hover', btnHover);
    root.style.setProperty('--button-border-radius', `${buttons.borderRadius}px`);

  }, [colors, typography, buttons, logo?.size, layout?.productGridColumns]);

  useEffect(() => {
    document.title = businessName || 'eCommerce';
    const faviconElement = document.querySelector("link[rel='icon']");

    if (logo?.logoType === 'logo') {
      if (logo?.faviconPath) {
        faviconElement.href = logo.faviconPath;
      }
    } else {
      const svg = `
        <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
          <rect width="100" height="100" fill="#fff"/>
          <text x="50%" y="50%" font-size="120" text-anchor="middle" stroke="${colors.primary}" stroke-width="1px" dy=".3em" fill="${colors.body}">
            ${logo?.logoText?.charAt(0) || 'Z'}
          </text>
        </svg>
      `;
      const svgBlob = new Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(svgBlob);
      faviconElement.href = url;
    }
  }, [businessName, logo, colors]);

  return null;
};

export default DynamicStyles;
